// Display formatters — wire format is int64 minor units rendered as
// decimal strings. PRICE_PRECISION (4dp) is the *storage* scale for
// both prices and wallet amounts. The matching engine ticks in 1/10,000
// so adjacent probability ticks can sit sub-cent apart (e.g. 50.25¢ vs
// 50.26¢). Wallet money lives at the same scale because reserves are
// `price * qty` — keeping one shared scale avoids rounding leaks at
// the order/ledger boundary.
//
// Display is different from storage. fmtMoney clamps to 2dp because
// users read currency in cents, not basis-points-of-a-cent. fmtPrice
// keeps the full 4dp because that's the operator's view into the
// engine's tick granularity. Same conventions as web-client.
import i18n, { intlLocale } from "@/i18n";

export const PRICE_PRECISION = 4;

function divide(intStr: string, precision: number): number {
  if (!intStr) return 0;
  const n = Number(intStr);
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** precision;
}

// fmtPrice is the raw WIRE lens: the storage-scale 4dp figure exactly as
// the engine ticks it ("0.3000"). Keep it for engine-facing pages and
// debugging surfaces only — operator-facing price displays use
// formatPrice below (SM-461/SM-384).
export function fmtPrice(s: string | number | undefined, frac = PRICE_PRECISION): string {
  if (s === undefined || s === null || s === "") return "—";
  const n = typeof s === "number" ? s : divide(String(s), PRICE_PRECISION);
  return n.toLocaleString(intlLocale(), { minimumFractionDigits: frac, maximumFractionDigits: frac });
}

// fmtQty renders a share quantity. `s` is minor units at the market's
// amount_precision — ap=0 means whole shares, ap=2 means 0.01-share
// lots, so a wire "164" is 1.64 shares. Ported from web-client's fmtQty
// so an operator reading a position and a trader reading the same
// position see the same number; the default ap=0 keeps every existing
// call site rendering exactly as before.
export function fmtQty(
  s: string | number | undefined,
  amountPrecision = 0,
): string {
  if (s === undefined || s === null || s === "") return "—";
  const n = typeof s === "number" ? s : Number(s);
  if (!Number.isFinite(n)) return "—";
  const human = amountPrecision > 0 ? n / 10 ** amountPrecision : n;
  return human.toLocaleString(intlLocale(), { maximumFractionDigits: amountPrecision });
}

// priceDpForTick derives the PRICE display decimals from a market's
// tick: the fewest decimals that render one tick exactly, floored at 2
// (0.10, not 0.1) and capped at the wire precision 4 (SM-460 matrix,
// 03a decision log #4 — the SM-443 presentation contract). tick=100 →
// 2dp (0.01 steps), tick=10 → 3dp, tick=1 → 4dp.
export function priceDpForTick(tickSize: string | number | undefined | null): number {
  const minor = typeof tickSize === "number" ? tickSize : Number(tickSize);
  if (!Number.isFinite(minor) || minor <= 0) return 2;
  let step = minor / 10 ** PRICE_PRECISION; // tick on the 0–1 scale
  let dp = 0;
  while (dp < PRICE_PRECISION && Math.abs(step - Math.round(step)) > 1e-9) {
    step *= 10;
    dp++;
  }
  return Math.max(2, dp);
}

// formatPrice renders a wire price on the unitless 0–1 scale (SM-517): a
// price is a fraction of the resolution value, not money, so it carries no
// "$" and no currency mark — wire 3000 reads "0.30", wire 10000 "1.00".
// Money is drawn by <CurrencyAmount> with the currency's coin. dp follows
// the market's tick when given (SM-460 matrix); wire prices are integers so
// the division by 10^4 is exact. Was fmtPriceDollars, which printed "$0.30";
// renamed to match web-client's formatPrice (SM-986) so a "dollars" helper
// does not grow back.
export function formatPrice(
  s: string | number | undefined,
  tickSize?: string | number,
): string {
  if (s === undefined || s === null || s === "") return "—";
  const n = typeof s === "number" ? s / 10 ** PRICE_PRECISION : divide(String(s), PRICE_PRECISION);
  if (!Number.isFinite(n)) return "—";
  const dp = priceDpForTick(tickSize);
  return n.toLocaleString(intlLocale(), { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// Money display lives in src/lib/money.ts (digits, grouping, truncation —
// never rounding) and currency IDENTITY in @/currency + <CurrencyAmount>,
// which draw the operator's configured name, symbol, mark and precision. The
// old $USD fmtMoney was removed; the operator console no longer shows dollars.

export function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(intlLocale(), { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

/** A day without a time ("2 Jan 2027"), in the operator's locale. */
export function fmtDay(iso: string | undefined | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(intlLocale(), { dateStyle: "medium" });
  } catch { return iso; }
}

export function fmtRelative(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const relative = new Intl.RelativeTimeFormat(intlLocale(), {
      numeric: "always",
      style: "short",
    });
    // RelativeTimeFormat uses negative values for the past and positive
    // values for the future. Keep the existing nearest-unit thresholds.
    const direction = ms >= 0 ? -1 : 1;
    const s = Math.round(Math.abs(ms) / 1000);
    if (s < 60) return relative.format(direction * s, "second");
    const m = Math.round(s / 60);
    if (m < 60) return relative.format(direction * m, "minute");
    const h = Math.round(m / 60);
    if (h < 24) return relative.format(direction * h, "hour");
    return relative.format(direction * Math.round(h / 24), "day");
  } catch { return iso; }
}

// Translate the wire enum names (SCREAMING_SNAKE_CASE) into a label
// that reads like a sentence. Tables and badges should never expose
// the raw enum — those are an API contract concern, not an operator
// concern. Covers every status the backoffice surfaces: rules,
// topics/categories, users, admins, wallets, orders, positions.
const STATUS_LABELS: Record<string, string> = {
  // rule lifecycle
  DRAFT: "Draft",
  OPEN: "Open",
  CLOSED_PENDING_RESOLUTION: "Closing",
  RESOLVED: "Resolved",
  SETTLED: "Settled",
  CANCELED: "Canceled",
  // topic / category
  ACTIVE: "Active",
  ARCHIVED: "Archived",
  // user
  SUSPENDED: "Suspended",
  CLOSED: "Closed",
  // admin
  LOCKED: "Locked",
  // campaign / chain network
  DISABLED: "Disabled",
  // wallet
  FROZEN: "Frozen",
  // position / order extras
  PARTIALLY_FILLED: "Partially filled",
  FILLED: "Filled",
  PENDING: "Pending",
  // Order ledger — the pre-book in-flight states and the terminal set.
  // Spelled out rather than left to toTitle so "Pending engine" doesn't
  // render as "Pending Engine" beside the sentence-cased rest.
  NEW: "New",
  RESERVED: "Reserved",
  PENDING_ENGINE: "Pending engine",
  CANCEL_PENDING: "Cancel pending",
  REJECTED: "Rejected",
  EXPIRED: "Expired",
  FAILED: "Failed",
};

export function statusLabel(status: string | undefined | null): string {
  if (!status) return "—";
  const key = `statuses.${status}`;
  if (i18n.exists(key)) return i18n.t(key);
  return STATUS_LABELS[status] ?? toTitle(status);
}

// Ledger transaction kinds, as an operator reads them.
//
// The distinction that matters on the treasury screen (SM-335): a
// SYSTEM_DEPOSIT / SYSTEM_WITHDRAW is a MINT or BURN — platform money
// created or retired inside the closed system by an operator. A
// SYSTEM_CRYPTO_* row is real money crossing the platform boundary on a
// chain. Both land in the same Movements table, and rendering the raw
// enum left the two looking like variants of one thing. They are not:
// one is an accounting entry, the other has a transaction hash.
//
// The three-step withdrawal (hold → complete | release) is spelled out
// because the intermediate state is exactly what an operator is looking
// for when they ask why `available` dropped but nothing left the wallet.
const TX_KIND_LABELS: Record<string, string> = {
  // Manual treasury adjustments (SM-152).
  SYSTEM_DEPOSIT: "Mint",
  SYSTEM_WITHDRAW: "Burn",
  // On-chain treasury movement (SM-335).
  SYSTEM_CRYPTO_DEPOSIT: "Crypto deposit",
  SYSTEM_CRYPTO_WITHDRAW_HOLD: "Crypto withdrawal held",
  SYSTEM_CRYPTO_WITHDRAW_COMPLETE: "Crypto withdrawal sent",
  SYSTEM_CRYPTO_WITHDRAW_RELEASE: "Crypto withdrawal released",
};

/**
 * Display label for a ledger transaction `kind`. Unknown kinds fall
 * through to title case rather than rendering as an enum — a new kind
 * shipped by the ledger should read passably here before anyone gets
 * round to naming it.
 */
export function txKindLabel(kind: string | undefined | null): string {
  if (!kind) return "—";
  return TX_KIND_LABELS[kind] ?? toTitle(kind);
}

/** True for the on-chain kinds, which carry a chain-side counterpart. */
export function isCryptoTxKind(kind: string | undefined | null): boolean {
  return !!kind && kind.startsWith("SYSTEM_CRYPTO_");
}

/**
 * Last-resort label for a row whose name never resolved: enough of the
 * id to identify it without letting a 26-character ULID set the width
 * of a column that is supposed to read as prose.
 *
 * The tail is kept deliberately. A ULID's first 10 characters are its
 * timestamp, so every market created in the same second shares them —
 * a head-only truncation would render two different rows identically,
 * which is worse than showing nothing. Callers pair this with the full
 * id on hover/copy.
 */
export function shortId(id: string | undefined | null): string {
  if (!id) return "—";
  return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

function toTitle(s: string): string {
  return s
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Decode a base64 JSON value (used by system_configs which the
// backend stores as raw JSONB and returns base64-encoded).
export function decodeConfigValue(b64: string): unknown {
  try {
    const txt = atob(b64);
    return JSON.parse(txt);
  } catch {
    try { return atob(b64); } catch { return b64; }
  }
}
